import { Component, inject } from '@angular/core';
import { TruncateTextComponent } from 'src/app/shared/components/truncate-text/truncate-text.component';
import { NotificationsService } from 'src/app/core/services/notifications.service';
import { BehaviorSubject, map, Observable, switchMap } from 'rxjs';
import { NotificationMessageDto } from 'src/app/shared/dtos/notification-item.dto';
import { Store } from '@ngxs/store';
import { GetNotifications } from 'src/app/shared/store/states/notifications/notifications.actions';
import { NotificationState } from 'src/app/shared/store/states/notifications/notification.state';
import { AsyncPipe } from '@angular/common';
import { DatetimeShortenerPipe } from 'src/app/shared/pipes/datetime-shortener.pipe';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AnalyticsService } from 'src/app/core/services/analytics.service';
import { ContentListResponseDto } from 'src/app/shared/dtos/content-list.dto';
import { take } from 'rxjs/operators';
import { AnalyticsEventName } from 'src/app/core/enums/analytics.enum';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-notifications',
  imports: [
    TruncateTextComponent,
    AsyncPipe,
    DatetimeShortenerPipe,
    RouterLink,
    TranslocoPipe,
  ],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
})
export class NotificationsComponent {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly store = inject(Store);
  protected readonly notificationsService = inject(NotificationsService);

  protected descriptionLimit = 100;

  private readonly contentList$: Observable<ContentListResponseDto> = this.activatedRoute.parent!.data.pipe(
    map(data => data['contentList']),
  );

  private readonly refresh$ = new BehaviorSubject<boolean>(false);

  protected readonly notifications$: Observable<NotificationMessageDto[]> = this.refresh$.pipe(
    switchMap(() => this.store.dispatch(new GetNotifications(true))),
    switchMap(() => this.store.select(NotificationState.getNotifications)),
    map((notifications) => {
      return [...notifications].sort((a: NotificationMessageDto, b: NotificationMessageDto) => {
        return b.createdAt - a.createdAt;
      });
    }),
  );

  protected markAllAsRead(notifications: NotificationMessageDto[]): void {
    const unreadIds = notifications
      .filter((notification) => !notification.isRead)
      .map((notification) => notification.id);

    if (unreadIds.length === 0) {
      return;
    }

    this.markAsRead(unreadIds);
  }

  protected markAsRead(ids: number[]): void {
    this.notificationsService.markAsRead(ids).subscribe({
      next: () => {
        this.notificationsService.closeNotifications();
        this.refresh$.next(true);
      },
    });
  }

  protected handleNotificationClick(notification: NotificationMessageDto): void {
    this.markAsRead([notification.id]);

    this.contentList$.pipe(
      take(1),
    ).subscribe(contentList => {
      const range = contentList.content.filter(range => {
        return range.children.some(machine => machine.id === notification.machineId);
      })[0];

      const machine = range.children.find(machine => machine.id === notification.machineId);

      if (machine) {
        this.analyticsService.trackEvent(AnalyticsEventName.ItemClick, {
          screenName: 'notifications',
          itemName: 'notification',
          itemId: 'notification',
          jcbRangeId: range.id,
          jcbRangeName: range.name,
          jcbMachineId: machine.id,
          jcbMachineName: machine.title,
        });
      }
    });
  }
}
